/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 07-07-2026
 *
 * Gedeelde basis voor de node-gebonden feedback-iconen (normfeedback/simfeedback):
 * een icoon met een teller, dat bij hover een ballon opent met gepagineerde
 * meldingen (vorige/volgende/sluiten). Vervangt de bijna-identieke ng1
 * `normfeedback`/`simfeedback`-directives.
 *
 * Het icoon pulseert zodra er bij deze node een melding bij komt (golf 3b, herstel van de
 * regressie uit golf 2). De detectie zit hier omdat deze basis de `meldingen`-input bezit; de
 * bijbehorende CSS staat in de gedeelde `node-dom-icon`-mixin.
 */
import {Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject, signal} from '@angular/core';
import type {Core, NodeSingular} from 'cytoscape';
import {KeyedTimeout} from 'sihw-ngx';
import {syncNodeHighlight} from '../../lib/node-highlight';
import {DlModelCanvasComponent} from '../canvas/dl-modelcanvas/dl-modelcanvas.component';

/**
 * Eén feedbackmelding (normfeedback/simfeedback) voor een node. Tot golf 4f stond dit type in de
 * migratie-interface.
 */
export interface FeedbackMelding {
    type: string;
    tekst: string;
    /** Of deze melding er bij de laatst binnengekomen uitslag bíj kwam; bepaalt of het icoon pulseert. */
    isNieuw: boolean;
}

@Directive()
export abstract class BaseFeedbackIconComponent implements OnChanges, OnDestroy {

    /** De Cytoscape-node waarop de meldingen betrekking hebben */
    @Input({required: true}) node!: NodeSingular;

    /** De Cytoscape-`Core` waarin de node leeft, nodig voor de highlight-sync van relaties */
    @Input({required: true}) cy!: Core;

    /** De feedbackmeldingen voor deze node */
    @Input() meldingen: FeedbackMelding[] = [];

    /** Of dit type feedback op dit moment aan staat (toggle in de feedbackbalk) */
    @Input() aan = false;

    /** Meldt of de ballon van dit icoon open (true) of dicht (true→false) is, voor onderdrukking van de uitleg */
    @Output() subHover = new EventEmitter<boolean>();

    /** CSS-selector (t.o.v. het component-element) van het icoon, voor de ballon-uitlijning */
    protected abstract readonly iconSelector: string;

    /** Index van de huidige melding in de ballon */
    protected readonly meldingIndex = signal(0);

    /** Of de ballon open is */
    protected readonly dlgOpen = signal(false);

    /** Of het icoon moet pulseren omdat er een melding bij is gekomen */
    protected readonly animeer = signal(false);

    private readonly host: HTMLElement = inject(ElementRef).nativeElement;

    /** De canvas-"baas": bron van de replay-stand en van de animatielock tijdens laden/redraw */
    private readonly modelcanvas = inject(DlModelCanvasComponent);

    private overIcon = false;
    private overDlg = false;

    /** De sluitvertraging van de ballon ('hoverclose') */
    private readonly timers = new KeyedTimeout();

    private dialoogKnopGedrukt = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['meldingen']) {
            this.volgHuidigeMelding(changes['meldingen'].previousValue as FeedbackMelding[] | undefined);
            this.checkAnimatie();
        }
        if (changes['aan'] && !this.aan) {
            //een verborgen icoon animeert niet door en laat geen ballon achter (bv. bij het starten
            //van een simulatie, waarmee de normfeedback uit gaat)
            this.animeer.set(false);
            if (this.dlgOpen()) {
                this.sluitDlg();
            }
        }
    }

    ngOnDestroy(): void {
        this.timers.clearAll();
        this.syncHighlight(false);
    }

    /** Of het icoon getoond moet worden: input aan staat én er zijn meldingen */
    protected toonSymbool(): boolean {
        return this.aan && this.meldingen.length > 0;
    }

    /** Aantal meldingen */
    protected numMeldingen(): number {
        return this.meldingen.length;
    }

    /** Tekst van de huidige melding in de ballon */
    protected getDlgTekst(): string {
        if (!this.meldingen.length) return '';
        const i = Math.min(Math.max(0, this.meldingIndex()), this.meldingen.length - 1);
        return this.meldingen[i]?.tekst ?? '';
    }

    /** Of de Vorige-knop getoond kan worden */
    protected kanVorige(): boolean {
        return this.meldingen.length > 1 && this.meldingIndex() > 0;
    }

    /** Of de Volgende-knop getoond kan worden */
    protected kanVolgende(): boolean {
        return this.meldingen.length > 1 && this.meldingIndex() < this.meldingen.length - 1;
    }

    /** Klik op Vorige: verlaag de meldingIndex indien mogelijk */
    protected klikVorige(): void {
        if (!this.kanVorige()) return;
        this.meldingIndex.update(i => i - 1);
        this.dialoogKnopGedrukt = true;
    }

    /** Klik op Volgende: verhoog de meldingIndex, of sluit de ballon als het de laatste was */
    protected klikVolgende(): void {
        if (this.meldingIndex() < this.meldingen.length - 1) {
            this.meldingIndex.update(i => i + 1);
            this.dialoogKnopGedrukt = true;
        } else {
            this.sluitDlg();
            this.meldingIndex.set(0);
        }
    }

    /** Klik op Sluiten: sluit de ballon altijd */
    protected klikSluiten(): void {
        this.sluitDlg();
    }

    /** Geeft het DOM-anker (icoon) terug waarop de ballon moet uitlijnen */
    protected getAnker(): HTMLElement | null {
        const el = this.host.querySelector<HTMLElement>(this.iconSelector);
        return (el && el.offsetParent !== null) ? el : null;
    }

    /** Eventhandler voor pointerenter op het icoon; tijdens replay opent de ballon niet */
    protected mouseInIcon(): void {
        if (this.modelcanvas.isInReplay()) return;
        this.overIcon = true;
        this.timers.clear('hoverclose');
        if (!this.dlgOpen()) this.openDlg();
    }

    /** Eventhandler voor animationend op het icoon: de animatie is uitgelopen */
    protected animatieKlaar(): void {
        this.animeer.set(false);
    }

    /** Eventhandler voor pointerleave op het icoon */
    protected mouseOutIcon(): void {
        this.overIcon = false;
        this.timers.set('hoverclose', () => {
            if (!this.overDlg && this.dlgOpen() && !this.dialoogKnopGedrukt) this.sluitDlg();
        }, 150);
    }

    /** Handler: muis komt de ballon binnen */
    protected bijDlgIn(): void {
        this.overDlg = true;
        this.timers.clear('hoverclose');
    }

    /** Handler: muis verlaat de ballon */
    protected bijDlgUit(): void {
        this.overDlg = false;
        if (!this.overIcon && this.dlgOpen() && !this.dialoogKnopGedrukt) this.sluitDlg();
    }

    /** Opent de ballon en meldt de groep-hover-onderdrukking */
    private openDlg(): void {
        if (this.dlgOpen()) return;
        this.dlgOpen.set(true);
        this.dialoogKnopGedrukt = false;
        this.subHover.emit(true);
        this.syncHighlight(true);
    }

    /** Sluit de ballon en meldt het einde van de groep-hover-onderdrukking */
    private sluitDlg(): void {
        this.dlgOpen.set(false);
        this.overDlg = false;
        this.timers.clear('hoverclose');
        this.dialoogKnopGedrukt = false;
        this.subHover.emit(false);
        this.syncHighlight(false);
    }

    /**
     * Of er bij deze node een melding bij is gekomen. De vraag wordt beantwoord door de lijst zelf:
     * de eigenaar van de data zet `isNieuw` op het moment dat die data binnenkomt, waar zeker is
     * waar hij vandaan komt. Dit component telt dus niets meer — dat ging mis zodra hij bij een
     * hertekening vers begon terwijl de uitslag van vóór die hertekening stamde.
     */
    protected heeftNieuweMelding(): boolean {
        return this.meldingen.some(melding => melding.isNieuw);
    }

    /**
     * Zet de lichte highlight aan of uit op de node (en bij een relatie ook op de
     * gekoppelde edges), via de gedeelde `syncNodeHighlight`-util.
     */
    private syncHighlight(aan: boolean): void {
        syncNodeHighlight(this.node, this.cy, this, aan);
    }

    /**
     * Houdt de ballon op de melding die hij toont. Bestaat die nog, dan schuift de index mee;
     * verdwijnt hij — of blijft er niets over — dan sluit de ballon. Voorheen sprong hij bij elke
     * wijziging terug naar de eerste melding en bleef hij leeg openstaan als er niets meer was.
     */
    private volgHuidigeMelding(vorige: FeedbackMelding[] | undefined): void {
        const getoond = vorige?.[this.meldingIndex()];
        const nieuweIndex = getoond
            ? this.meldingen.findIndex(melding => melding.type === getoond.type && melding.tekst === getoond.tekst)
            : -1;
        if (nieuweIndex >= 0) {
            this.meldingIndex.set(nieuweIndex);
            return;
        }
        this.meldingIndex.set(0);
        if (this.dlgOpen()) {
            this.sluitDlg();
        }
    }

    /**
     * Laat het icoon pulseren als er bij deze node een melding bij is gekomen. Niet tijdens het
     * laden of hertekenen van een model (de blokkade van het canvas) en niet tijdens replay: dan
     * komen alle meldingen in bulk binnen en zou het halve canvas knipperen.
     */
    private checkAnimatie(): void {
        if (this.heeftNieuweMelding() && this.aan && !this.modelcanvas.isInReplay()
            && !this.modelcanvas.zijnAnimatiesGeblokkeerd()) {
            this.animeer.set(true);
        }
    }
}
